import React, { useCallback, useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import { ColorIndicator, StackContainer } from './style';
import { abyssColors } from '@/theme/colors';

type DataItem = {
  _id: string;
  count: number;
};

type DisplayConfigItem = {
  fieldValue: string;
  color: string;
};

type CircularBarChartProps = {
  data: DataItem[];
  displayConfig: DisplayConfigItem[];
};

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describeArc = (
  x: number,
  y: number,
  outerRadius: number,
  innerRadius: number,
  startAngle: number,
  endAngle: number
) => {
  const startOuter = polarToCartesian(x, y, outerRadius, endAngle);
  const endOuter = polarToCartesian(x, y, outerRadius, startAngle);
  const startInner = polarToCartesian(x, y, innerRadius, endAngle);
  const endInner = polarToCartesian(x, y, innerRadius, startAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return [
    'M',
    startOuter.x,
    startOuter.y,
    'A',
    outerRadius,
    outerRadius,
    0,
    largeArcFlag,
    0,
    endOuter.x,
    endOuter.y,
    'L',
    endInner.x,
    endInner.y,
    'A',
    innerRadius,
    innerRadius,
    0,
    largeArcFlag,
    1,
    startInner.x,
    startInner.y,
    'L',
    startOuter.x,
    startOuter.y,
    'Z',
  ].join(' ');
};

export const CircularBarChart: React.FC<CircularBarChartProps> = ({ data, displayConfig }) => {
  const [selectedLevel, setSelectedLevel] = useState<string | undefined>(undefined);

  const handleLevelSelection = (level: string) => {
    setSelectedLevel(selectedLevel === level ? undefined : level);
  };

  const getColorByValue = useCallback(
    (value: string) => {
      const config = displayConfig.find(
        (configItem) => configItem.fieldValue.toUpperCase() === value
      );
      return config ? config.color : '#000'; // Default color if not found
    },
    [displayConfig]
  );

  const totalValue = useMemo(() => {
    return data.reduce((accumulator, item) => accumulator + item.count, 0);
  }, [data]);

  const chartData = useMemo(() => {
    let cumulativeValue = 0;
    const spaceBetweenArcs = 2;

    return data.map((item, index) => {
      const startAngle = (cumulativeValue / totalValue) * 360;
      cumulativeValue += item.count;
      let endAngle = (cumulativeValue / totalValue) * 360;

      if (data.length === 1 || endAngle === startAngle) {
        endAngle = startAngle + 359.99;
      }

      return {
        ...item,
        color: getColorByValue(item._id),
        startAngle: index === 0 ? startAngle : startAngle + spaceBetweenArcs,
        endAngle: endAngle - spaceBetweenArcs,
      };
    });
  }, [data, getColorByValue, totalValue]);

  if (data.length === 0)
    return (
      <Typography
        variant="h6"
        sx={{
          color: abyssColors.primary[400],
          whiteSpace: 'nowrap',
          mt: 1,
        }}
      >
        No data found
      </Typography>
    );

  return (
    <Stack direction="row" alignItems="center">
      <Box sx={{ marginLeft: '-32px !important' }}>
        <svg width="100" height="100" viewBox="0 0 100 100">
          {chartData.map((slice, index) => (
            <path
              key={index}
              d={describeArc(50, 50, 40, 25, slice.startAngle, slice.endAngle)}
              fill={slice.color}
              onClick={() => handleLevelSelection(slice._id)}
              style={{ cursor: 'pointer' }}
            />
          ))}
          <circle cx="50" cy="50" r="20" fill="#fff" />
        </svg>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center">
        {chartData.map((item) => (
          <StackContainer
            onClick={() => handleLevelSelection(item._id)}
            key={item._id}
            selected={selectedLevel === item._id}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {item.count}
            </Typography>
            <Stack direction="row" alignItems="center">
              <ColorIndicator color={item.color} />
              <Typography variant="caption" sx={{ color: '#425265' }}>
                {item._id.replace(/_/g, ' ')}
              </Typography>
            </Stack>
          </StackContainer>
        ))}
      </Stack>
    </Stack>
  );
};
